
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/base/Button';

export default function ServicesPage() {
  const navigate = useNavigate();

  const services = [
    {
      icon: 'ri-plant-line',
      title: 'Frozen Fruits & Vegetables Supply',
      description: 'Premium quality frozen produce sourced from the finest farms worldwide. Maintaining nutritional value and freshness through advanced freezing technology.',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: 'ri-flask-line',
      title: 'Freeze-Dried Solutions',
      description: 'State-of-the-art freeze-drying processes that preserve nutrients, extend shelf life, and maintain original taste and texture.',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      icon: 'ri-gift-line',
      title: 'Private Label & Packaging',
      description: 'Custom packaging solutions and private label services tailored to your brand requirements and market specifications.',
      color: 'from-purple-500 to-violet-600'
    },
    {
      icon: 'ri-truck-line',
      title: 'Global Logistics & Cold Chain',
      description: 'End-to-end cold chain management ensuring product integrity from our facilities to your destination worldwide.',
      color: 'from-orange-500 to-amber-600'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Quality & Certifications',
      description: 'International quality certifications including HACCP, ISO, BRC, and organic standards ensuring compliance and safety.',
      color: 'from-red-500 to-rose-600'
    },
    {
      icon: 'ri-global-line',
      title: 'Custom B2B Export Solutions',
      description: 'Tailored export solutions for B2B clients including documentation, compliance, and market-specific requirements.',
      color: 'from-teal-500 to-green-600'
    }
  ];

  const highlights = [
    {
      image: 'https://readdy.ai/api/search-image?query=modern%20industrial%20food%20processing%20facility%20with%20conveyor%20belts%20carrying%20colorful%20frozen%20fruits%20and%20vegetables%2C%20workers%20in%20white%20uniforms%20and%20hairnets%2C%20bright%20clean%20environment%2C%20stainless%20steel%20equipment%2C%20professional%20food%20safety%20standards&width=800&height=600&seq=1&orientation=landscape',
      title: 'Advanced Processing Facilities',
      description: 'Our state-of-the-art processing facilities utilize cutting-edge technology to ensure maximum quality retention. From blast freezing to freeze-drying, every step is monitored and controlled to deliver products that meet international standards.',
      features: ['24/7 Temperature Monitoring', 'Automated Quality Control', 'Traceability Systems']
    },
    {
      image: 'https://readdy.ai/api/search-image?query=massive%20refrigerated%20warehouse%20with%20tall%20shelving%20systems%20filled%20with%20frozen%20food%20products%2C%20industrial%20cooling%20systems%20visible%2C%20workers%20operating%20forklifts%2C%20organized%20inventory%20management%2C%20cold%20storage%20facility&width=800&height=600&seq=2&orientation=landscape',
      title: 'Cold Chain Excellence',
      description: 'Our comprehensive cold chain network spans from farm to destination, ensuring unbroken temperature control throughout the journey. Advanced logistics planning and real-time monitoring guarantee product integrity.',
      features: ['Temperature-Controlled Transport', 'Real-time Tracking', 'Global Distribution Network']
    },
    {
      image: 'https://readdy.ai/api/search-image?query=professional%20laboratory%20with%20scientists%20in%20white%20coats%20testing%20frozen%20food%20samples%2C%20modern%20testing%20equipment%2C%20quality%20control%20analysis%2C%20microscopes%20and%20testing%20devices%2C%20clean%20sterile%20environment&width=800&height=600&seq=3&orientation=landscape',
      title: 'Quality Assurance Lab',
      description: 'Our in-house laboratory conducts rigorous testing at every stage of production. From raw material inspection to final product verification, we ensure compliance with international food safety standards.',
      features: ['Microbiological Testing', 'Nutritional Analysis', 'Contaminant Screening']
    }
  ];

  const whyChooseUs = [
    {
      icon: 'ri-time-line',
      title: '25+ Years Experience',
      description: 'Decades of expertise in frozen food export and international trade.',
      stat: '25+'
    },
    {
      icon: 'ri-earth-line',
      title: '50+ Countries Served',
      description: 'Global reach with established distribution networks worldwide.',
      stat: '50+'
    },
    {
      icon: 'ri-award-line',
      title: 'Certified Quality Standards',
      description: 'Multiple international certifications ensuring top quality and safety.',
      stat: '15+'
    },
    {
      icon: 'ri-leaf-line',
      title: 'Sustainability Commitment',
      description: 'Eco-friendly practices and sustainable sourcing initiatives.',
      stat: '100%'
    }
  ];

  return (
    <main className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://readdy.ai/api/search-image?query=fresh%20colorful%20fruit%20and%20vegetable%20pattern%20background%20with%20strawberries%20blueberries%20carrots%20broccoli%20scattered%20naturally%2C%20light%20pastel%20colors%2C%20organic%20food%20illustration%2C%20healthy%20produce%20display%2C%20clean%20minimalist%20style&width=1920&height=1080&seq=4&orientation=landscape)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/90 to-white/95"></div>
        
        {/* Floating Fruit Doodles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-16 h-16 opacity-20"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`
              }}
              animate={{
                y: [0, -20, 0],
                rotate: [0, 5, -5, 0],
              }}
              transition={{
                duration: 4 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2
              }}
            >
              <i className={`ri-${['apple', 'leaf', 'plant', 'seedling'][Math.floor(Math.random() * 4)]}-line text-4xl text-primary-600`}></i>
            </motion.div>
          ))}
        </div>

        <div className="relative z-10 text-center max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-6"
          >
            <span className="inline-block px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold tracking-wide uppercase">
              Premium Export Solutions
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-5xl md:text-7xl font-bold text-gray-800 mb-6 leading-tight"
          >
            Our
            <span className="bg-gradient-to-r from-primary-600 to-green-600 bg-clip-text text-transparent"> Services</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto"
          >
            Comprehensive frozen food export solutions connecting premium quality produce 
            with global markets through advanced technology and reliable logistics.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button 
              size="lg" 
              onClick={() => navigate('/contact')}
              className="bg-gradient-to-r from-primary-600 to-green-600 hover:from-primary-700 hover:to-green-700 text-white shadow-xl"
            >
              Get Quote
              <i className="ri-arrow-right-line ml-2"></i>
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => navigate('/products')}
              className="border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white"
            >
              View Products
            </Button>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gray-600 flex flex-col items-center"
        >
          <span className="text-sm mb-2 font-medium">Explore Our Services</span>
          <motion.i
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="ri-arrow-down-line text-2xl"
          ></motion.i>
        </motion.div>
      </section>

      {/* Services Grid Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Complete Export Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              From sourcing to delivery, we handle every aspect of frozen food export 
              with precision and expertise.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer border border-gray-100"
              >
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${service.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${service.icon} text-2xl text-white`}></i>
                </div>
                
                <h3 className="text-xl font-bold text-gray-800 mb-4 group-hover:text-primary-600 transition-colors duration-300">
                  {service.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed">
                  {service.description}
                </p>

                <motion.div
                  initial={{ width: 0 }}
                  whileHover={{ width: '100%' }}
                  className="h-1 bg-gradient-to-r from-primary-600 to-green-600 rounded-full mt-6 transition-all duration-300"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Highlights Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Service Highlights
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Behind every successful export is our commitment to excellence in processing, 
              logistics, and quality assurance.
            </p>
          </motion.div>

          <div className="space-y-20">
            {highlights.map((highlight, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className={`flex flex-col ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } items-center gap-12`}
              >
                <div className="flex-1">
                  <div className="relative">
                    <img
                      src={highlight.image}
                      alt={highlight.title}
                      className="w-full h-96 object-cover rounded-3xl shadow-2xl"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-3xl"></div>
                  </div>
                </div>

                <div className="flex-1">
                  <motion.div
                    initial={{ opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                  >
                    <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                      {highlight.title}
                    </h3>
                    
                    <p className="text-lg text-gray-600 leading-relaxed mb-8">
                      {highlight.description}
                    </p>

                    <div className="space-y-4">
                      {highlight.features.map((feature, featureIndex) => (
                        <motion.div
                          key={featureIndex}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: 0.6 + featureIndex * 0.1 }}
                          className="flex items-center gap-3"
                        >
                          <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                          <span className="text-gray-700 font-medium">{feature}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section 
        className="py-20 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://readdy.ai/api/search-image?query=subtle%20hand-drawn%20fruit%20and%20vegetable%20doodles%20pattern%2C%20light%20sketch%20style%20illustrations%20of%20apples%20oranges%20carrots%20broccoli%2C%20very%20light%20gray%20lines%20on%20white%20background%2C%20minimalist%20organic%20food%20sketches&width=1920&height=1080&seq=5&orientation=landscape)'
        }}
      >
        <div className="absolute inset-0 bg-white/95"></div>
        
        <div className="relative container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Why Choose FrozenExport?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Trust our proven track record and commitment to excellence in 
              international frozen food trade.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-2">
                  <div className="w-20 h-20 bg-gradient-to-r from-primary-600 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${item.icon} text-3xl text-white`}></i>
                  </div>

                  <div className="text-4xl font-bold text-primary-600 mb-2">
                    {item.stat}
                  </div>

                  <h3 className="text-xl font-bold text-gray-800 mb-4">
                    {item.title}
                  </h3>

                  <p className="text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section 
        className="py-20 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://readdy.ai/api/search-image?query=modern%20corporate%20office%20meeting%20room%20with%20large%20conference%20table%2C%20business%20professionals%20discussing%20export%20documents%2C%20world%20map%20on%20wall%2C%20professional%20business%20environment%2C%20clean%20corporate%20setting&width=1920&height=1080&seq=6&orientation=landscape)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/90 to-green-900/95"></div>
        
        <div className="relative container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Looking for a reliable 
              <span className="text-primary-300"> export partner?</span>
            </h2>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Let's discuss your frozen food export requirements and create a customized solution 
              that meets your business goals.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Button 
                size="lg" 
                onClick={() => navigate('/contact')}
                className="bg-white text-primary-600 hover:bg-gray-100 font-semibold shadow-xl hover:shadow-2xl"
              >
                Start Your Export Journey
                <i className="ri-rocket-line ml-2"></i>
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => navigate('/about')}
                className="border-2 border-white text-white hover:bg-white hover:text-primary-600"
              >
                Learn More About Us
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 text-center"
            >
              <div className="text-white">
                <div className="text-3xl font-bold text-primary-300 mb-2">24/7</div>
                <div className="text-gray-200">Customer Support</div>
              </div>
              <div className="text-white">
                <div className="text-3xl font-bold text-primary-300 mb-2">&lt;48h</div>
                <div className="text-gray-200">Quote Response Time</div>
              </div>
              <div className="text-white">
                <div className="text-3xl font-bold text-primary-300 mb-2">100%</div>
                <div className="text-gray-200">Quality Guarantee</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
